import React, { useState } from 'react';
import styled from 'styled-components';
import { isEmpty } from 'lodash-es';

import List from 'shared/components/List';
import { SafelistTerm as SafelistTermType } from 'shared/types/safelist';

import { SafelistTermListHeader } from './SafelistTermListHeader';
import { SEARCH_FILTER_KEY, MULTI_OPTION_SELECT_FIELDS } from '../../constants';
import { SafelistTermListHeaderFilter } from './SafelistTermListHeader/types';
import { SafelistTerm as PureSafelistTerm } from './SafelistTerm';
import { SafelistTermListProps } from './types';

const LIST_ITEM_WRAPPER_CLASS_NAME = 'safelist-term-manager__list-item-wrapper';

const StyledSafelistTermListContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const StyledSafelistTermListHeader = styled(SafelistTermListHeader)``;

const StyledList = styled(List)`
    & {
        .${LIST_ITEM_WRAPPER_CLASS_NAME} {
            display: inline-flex;
            margin: 0 0.5rem 0.5rem 0;
        }
    }
`;

// Styling the component to be able to apply style from its container
const SafelistTerm = styled(PureSafelistTerm)``;

const StyledSafelistTermListContent = styled.div`
    flex: 1;
    display: flex;
    padding: 1px;
    overflow-x: hidden;
    overflow-y: auto;
`;

const StyledNoTermsContainer = styled.div`
    flex: 1;
    display: flex;
    justify-content: center;
    align-items: center;
`;

const StyledNoTermsMessage = styled.span`
  font-size: 1rem;
  color: #8d9199;
`;

const filterTerms = (terms: SafelistTermType[], filters: SafelistTermListHeaderFilter) => {
    // Split the filter object into the search term, which has an 'includes' logic, and the rest of filters which require exact matches
    const {
        [SEARCH_FILTER_KEY]:searchTerm = '',
        ...otherFilters    
    } = filters;

    if (emptyFilter(filters)) {
        return terms;
    }

    return terms
        .filter((t, _, termArray) => {
            // If there is no a search term, we return the original array.
            if (!searchTerm) {
                return termArray;
            }

            // If not, we filter those terms that match the search term.
            return t[SEARCH_FILTER_KEY].match(new RegExp(searchTerm, 'i'));
        })
        .filter((t, _, termArray) => {
            // Same as before, if no type was selected, we return the original array.
            if (isEmpty(otherFilters)) {
                return termArray
            }

            // Otherwise, if a type was selected, we filter the terms whose type matches the selected type.
            return MULTI_OPTION_SELECT_FIELDS.every((multiOptionField: Exclude<keyof SafelistTermType, "entity">, index: number) => {
                return (otherFilters[multiOptionField] || '').includes(t[multiOptionField] as string);
            });
        });
};

const SafelistTermList = ({
    className,
    terms = [],
    onDelete,
}: SafelistTermListProps) => {
    const [filters, setFilters] = useState<SafelistTermListHeaderFilter>({});

    const filteredTerms = filterTerms(terms, filters);

    return (
        <StyledSafelistTermListContainer className={className}>
            <StyledSafelistTermListHeader
                filters={filters}
                onFilterChange={(filters) => setFilters(filters)}
            />
            <StyledSafelistTermListContent>
                {filteredTerms.length
                    ? <StyledList
                        itemWrapperClassName={LIST_ITEM_WRAPPER_CLASS_NAME}
                        items={filteredTerms}
                        renderItem={term => (
                            <SafelistTerm
                                term={term}
                                onDelete={onDelete}
                            />
                        )}
                      />
                    : <StyledNoTermsContainer>
                        <StyledNoTermsMessage>
                            Add safelist items to the library.
                        </StyledNoTermsMessage>
                      </StyledNoTermsContainer>
                }
            </StyledSafelistTermListContent>
        </StyledSafelistTermListContainer>
    );
};

// Helper Functions

// Checks filter object to determine if all filters are empty/null and should reset
const emptyFilter = (filterObject: SafelistTermListHeaderFilter) => {
    if (isEmpty(filterObject)) return true;

    // This is basic and should be extended if component is reused
    return Object.values(filterObject).every(isEmpty);
};



export { SafelistTermList };
